import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';
import moment from 'moment';

import type {TaskCreatorEntity} from 'nutshell-core/tasks/tasks-types';

import {Button} from 'shells/button/button';
import {Body} from 'shells/typography/body';

import {StepDelayDialog} from '../../../../../ui/master-dashboard/sidebar/todos/step-delay-dialog';

import {colors} from '../colors/colors';
import {Description} from '../description/description';
import {Dialog} from '../dialog/dialog';
// @ts-expect-error - imported from .js/.jsx file
import {EditableTimestamp} from '../editable-timestamp/editable-timestamp';
// @ts-expect-error - imported from .js/.jsx file
import {StepIcon} from '../icon/svg/step-icon';
import {SyncRegularIcon} from '../icon/svg/sync-regular-icon';
import {TaskRegularAiIcon} from '../icon/svg/task-regular-ai-icon';
import {LinkedEntity} from '../linked-entity/linked-entity';
import {LinkedLeadEntity} from '../linked-entity/linked-lead-entity';

import styles from './todo-item.module.css';

const typeIconMap = {
    steps: StepIcon,
    taskRecurring: SyncRegularIcon,
    tasks: null,
} as const;

type Props = {
    todo: {
        id: string;
        type: string;
        title: string;
        description?: string;
        relatedEntity?: any;
        dueTime?: number; // unix seconds,
        isCompleted: boolean;
        isOverdue: boolean;
        isSkipped?: boolean;
        isSkippable?: boolean;
        isDelayable?: boolean;
        isRecurring?: boolean;
        isAiGenerated?: boolean;
        creatorEntity?: TaskCreatorEntity | null;
        isCompleteable?: boolean;
        requirementText?: string;
        delayApplications?: any[];
    };
    hasActions?: boolean;
    onUncomplete: (...args: any) => any;
    onComplete: (...args: any) => any;
    onUncompleteConfirm: (...args: any) => any;
    onUncompleteCancel: (...args: any) => any;
    onDelayConfirm: (...args: any) => any;
    onDelayCancel: (...args: any) => any;
    onSkipConfirm: (...args: any) => any;
    onSkipCancel: (...args: any) => any;
    onDeleteConfirm: (...args: any) => any;
    onDeleteCancel: (...args: any) => any;
    onTimeChange: (...args: any) => any;
    isUncompleting: boolean;
    isDelaying: boolean;
    isSkipping: boolean;
    isDeleting: boolean;
};

export class TodoItem extends React.Component<Props> {
    container: HTMLElement | null | undefined;
    checkbox: HTMLElement | null | undefined;

    componentDidMount() {
        ReactTooltip.rebuild();
    }

    componentDidUpdate() {
        ReactTooltip.rebuild();
    }

    render() {
        const {
            type,
            title,
            dueTime,
            description,
            relatedEntity,
            isCompleted,
            isOverdue,
            isSkipped,
            isAiGenerated,
            creatorEntity,
            requirementText,
            isRecurring,
            delayApplications,
        } = this.props.todo;

        const styleNames = classnames(styles.container, {
            [styles['container-skipped']]: isSkipped,
            [styles['container-completed']]: isCompleted,
            [styles['container-overdue']]: isOverdue,
            [styles['container-uncompleteable']]: !this.canCompleteTodo(),
        });

        let todoType = type;
        if (todoType === 'tasks' && isRecurring) {
            todoType = 'taskRecurring';
        }

        let secondaryText = '';
        if (isSkipped) {
            secondaryText = 'Skipped';
        } else if (delayApplications && delayApplications.length) {
            secondaryText = `Delayed ${delayApplications.length} times`;
        }
        const formattedTimestamp = dueTime ? moment.unix(dueTime).format('h:mma') : null;

        // @ts-expect-error
        const TypeIconComponent = typeIconMap[todoType];
        const showAiTaskInfo = NutClientConfig.hasAiFollowUpTasksFF && isAiGenerated;

        return (
            <div
                ref={(c) => {
                    this.container = c;
                }}
                className={styleNames}
            >
                {this.props.hasActions ? (
                    <div className={styles['left-container']}>
                        <div
                            data-tooltip={requirementText}
                            className={
                                requirementText
                                    ? styles['checkbox-container--with-tooltip']
                                    : styles['checkbox-container']
                            }
                        >
                            <div
                                ref={(c) => {
                                    this.checkbox = c;
                                }}
                                onClick={this.handleCheckboxClick}
                                className={styles.checkbox}
                            />
                        </div>
                        <div
                            className={styles['type-icon']}
                            data-tip={
                                showAiTaskInfo && creatorEntity
                                    ? `Created as a follow-up from ${creatorEntity.name}`
                                    : undefined
                            }
                            data-place='bottom'
                        >
                            {showAiTaskInfo ? (
                                <TaskRegularAiIcon
                                    size={16}
                                    // @ts-expect-error
                                    color={colors.aiPurplePrimary}
                                    alignCenter={true}
                                    wrapWithDiv={false}
                                />
                            ) : TypeIconComponent ? (
                                <TypeIconComponent size={10} />
                            ) : undefined}
                        </div>
                    </div>
                ) : undefined}
                <div className={styles['content-container']}>
                    <div title={title} className={styles['title-container']}>
                        <div className={styles.title}>{title}</div>
                        {description ? (
                            <span className={styles.description}>
                                <Description description={description} />
                            </span>
                        ) : null}
                    </div>
                    <div className={styles['related-entity-container']}>
                        {relatedEntity && relatedEntity.id && relatedEntity.type === 'leads' ? (
                            <LinkedLeadEntity
                                key={relatedEntity.id}
                                // @ts-expect-error
                                stopPropagation={true}
                                lead={relatedEntity}
                                showIcon={true}
                            />
                        ) : null}
                        {relatedEntity && relatedEntity.id && relatedEntity.type !== 'leads' ? (
                            <LinkedEntity
                                key={relatedEntity.id}
                                stopPropagation={true}
                                {...relatedEntity}
                            />
                        ) : null}
                    </div>
                    {NutClientConfig.hasAiFollowUpTasksFF && creatorEntity ? (
                        <div className={styles['creator-entity-container']}>
                            <Body color='light' size='small'>
                                via
                            </Body>{' '}
                            <div className={styles['creator-entity-button-container']}>
                                <Button
                                    className={styles['creator-entity-button']}
                                    variant='text-primary'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        window.location.hash = `/${creatorEntity.id}`;
                                    }}
                                >
                                    {creatorEntity.name}
                                </Button>
                            </div>
                        </div>
                    ) : null}
                </div>
                <div className={styles['right-container']}>
                    {dueTime && formattedTimestamp ? (
                        <div className={styles['timestamp-container']}>
                            <EditableTimestamp
                                formattedValue={formattedTimestamp}
                                value={dueTime}
                                onTimeChange={
                                    type === 'tasks' ? this.props.onTimeChange : undefined
                                }
                            />
                        </div>
                    ) : undefined}
                    {secondaryText ? (
                        <div className={styles['secondary-text']}>{secondaryText}</div>
                    ) : undefined}
                </div>
                {this.props.isUncompleting ? (
                    <Dialog
                        anchor={this.checkbox}
                        location='bottom'
                        question='Are you sure you want to uncomplete?'
                        onPrimary={this.props.onUncompleteConfirm}
                        onSecondary={this.props.onUncompleteCancel}
                    />
                ) : undefined}
                {this.props.isSkipping ? (
                    <Dialog
                        anchor={this.container}
                        location='bottom'
                        question='Are you sure you want to skip this task?'
                        onPrimary={this.props.onSkipConfirm}
                        onSecondary={this.props.onSkipCancel}
                    />
                ) : undefined}
                {this.props.isDelaying ? (
                    <StepDelayDialog
                        anchor={this.container}
                        // @ts-expect-error
                        location='left'
                        stepId={this.props.todo.id}
                        onApply={this.props.onDelayConfirm}
                        onCancel={this.props.onDelayCancel}
                    />
                ) : undefined}
                {this.props.isDeleting ? (
                    <Dialog
                        anchor={this.container}
                        location='bottom'
                        isWarning={true}
                        question='Are you sure you want to delete this task?'
                        onPrimary={this.props.onDeleteConfirm}
                        onSecondary={this.props.onDeleteCancel}
                    />
                ) : undefined}
            </div>
        );
    }

    handleCheckboxClick = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
        e.preventDefault();

        if (!this.canCompleteTodo()) return;

        if (this.props.todo.isCompleted) {
            this.props.onUncomplete();
        } else {
            this.props.onComplete();
        }
    };

    canCompleteTodo = () => {
        return this.props.todo.isCompleteable || this.props.todo.type === 'tasks';
    };
}
